import { createContext, type ReactNode, useContext } from "react";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverTitle,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import {
  MISSING,
  MISSING_LABEL,
  missingNote,
  missingReason,
  partialNote,
} from "./missing";
import type { PartialFigure } from "./output-types";
import type { ReportInputSlot } from "./types";

const InputsContext = createContext<Pick<ReportInputSlot, "key" | "title">[]>(
  [],
);

/**
 * The template's inputs (`report.inputs`), so a missing figure whose column
 * names its `source` can say which file it is not in.
 */
export const ReportInputsProvider = ({
  inputs,
  children,
}: {
  inputs: Pick<ReportInputSlot, "key" | "title">[] | null | undefined;
  children: ReactNode;
}) => (
  <InputsContext.Provider value={inputs ?? []}>
    {children}
  </InputsContext.Provider>
);

export const useReportInputs = () => useContext(InputsContext);

/** Null when the template has no input with this key. */
export const inputTitle = (
  inputs: Pick<ReportInputSlot, "key" | "title">[],
  key: string | null | undefined,
) => (key && inputs.find((input) => input.key === key)?.title) || null;

export const useMissingReason = (source?: string | null) =>
  missingReason(inputTitle(useReportInputs(), source));

/**
 * A figure that is missing, as a dash a screen reader reads as "Missing".
 * Never zero: see `MISSING`. A cell's dash (`source` given, `null` for a
 * column that names none) says why in its tooltip: not in this period's
 * files, or not in the one input the column comes from.
 */
export const MissingValue = ({
  className,
  source,
}: {
  className?: string;
  /** The column's `source`; leave out for a dash that is not a cell's. */
  source?: string | null;
}) => {
  const reason = useMissingReason(source);
  const dash = (
    <>
      <span aria-hidden>{MISSING}</span>
      <span className="sr-only">{MISSING_LABEL}</span>
    </>
  );
  if (source === undefined)
    return (
      <span title={MISSING_LABEL} className={cn("text-subtle", className)}>
        {dash}
      </span>
    );
  return (
    <Tooltip>
      <TooltipTrigger
        render={<span className={cn("text-subtle", className)} />}
      >
        {dash}
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
};

/**
 * A formatted figure, or the missing dash when the formatter said it was
 * missing (`formatReportValue`, `formatCell` and `formatSummaryValue` return
 * `MISSING` for a numeric cell without a value). Given its column, the dash
 * says why it is missing (`MissingValue`).
 */
export const showValue = (
  text: string,
  column?: { source?: string },
): ReactNode =>
  text === MISSING ? (
    <MissingValue source={column ? (column.source ?? null) : undefined} />
  ) : (
    text
  );

/**
 * A missing field on a record: "Missing" in quiet text after a warning dot,
 * the way a flag reads, so a gap is told apart from a figure and from zero.
 * Its tooltip, and a screen reader, say which file it is not in; a figure
 * worked out from missing ones (`needs`) says what it waits on instead:
 * "Missing — needs Labor", since the fix is Labor's, not its own.
 */
export const MissingFigure = ({
  source,
  needs,
}: {
  source?: string | null;
  needs?: string | null;
}) => {
  const missing = useMissingReason(source);
  const reason = needs ? `Can't be worked out without ${needs}` : missing;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span className="inline-flex min-w-0 items-start gap-1.5 text-sm font-normal whitespace-normal text-subtle" />
        }
      >
        <span
          aria-hidden
          className="mt-1.75 size-1.5 flex-none rounded-full bg-warning"
        />
        <span>
          {needs ? `${MISSING_LABEL} — needs ${needs}` : MISSING_LABEL}
        </span>
        <span className="sr-only">: {reason}</span>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
};

/**
 * Under a rolled-up figure that adds up only what is known, quietly: how
 * many records it is missing ("2 jobs missing"). Nothing when it is whole.
 */
export const PartialNote = ({
  partial,
}: {
  partial: PartialFigure | null | undefined;
}) => {
  const note = partial ? partialNote(partial.missing, partial.of) : null;
  if (!note) return null;
  return (
    <span className="block text-xs leading-normal font-normal text-subtle">
      {note}
    </span>
  );
};

export const MissingNote = ({
  count,
  label,
  className,
}: {
  count: number | null | undefined;
  label: string;
  className?: string;
}) => {
  const note = missingNote(count);
  if (!note) return null;
  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        aria-label={`${label}: ${note}`}
        onClick={(event) => event.stopPropagation()}
        className={cn(
          "relative min-h-6 rounded-xs text-start text-xs leading-normal font-normal text-subtle underline decoration-dotted underline-offset-4 before:absolute before:inset-x-0 before:-inset-y-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink pointer-coarse:min-h-11",
          className,
        )}
      >
        {note}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-w-[calc(100vw-2rem)] p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <PopoverTitle>Incomplete data</PopoverTitle>
        <PopoverDescription>
          Data is missing for {count?.toLocaleString()}{" "}
          {count === 1 ? "record" : "records"} used to calculate {label}. This
          figure uses the available data and may change when the missing data is
          added.
        </PopoverDescription>
      </PopoverContent>
    </Popover>
  );
};
